import { Icon, Input, Switch } from "@elaniintc/goes-design-system";
import type { NodeVariable, Variable } from "../../types";
import type { DocumentField } from "../types";
import { cn } from "@/utils/theme";
import ComparativeValueInputCalendar from "../../if/ComparativeValueInput.calendar";
import { VariableSelector } from "./VariableSelector";

export interface DocumentFieldValueInputProps {
  field: DocumentField;
  value: string | Variable;
  onValueChange: (value: string | Variable) => void;
  inputVariables: NodeVariable[];
  placeholder?: string;
  showInputIcon?: boolean;
  group?:
    | "middle"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down"
    | "middle-left"
    | "middle-right";
}

const getIconName = (fieldType: string): string => {
  switch (fieldType) {
    case "text":
      return "text";
    case "number":
      return "hashtag";
    case "date":
      return "calendar";
    case "boolean":
      return "toggle-large-on";
    case "variable":
      return "table";
    default:
      return "text";
  }
};

const getMaxLength = (fieldType: string) => {
  switch (fieldType) {
    case "text":
      return 255;
    case "number":
      return 15;
    default:
      return undefined;
  }
};

// Función helper para obtener placeholder según el tipo
const getPlaceholder = (fieldType: string): string => {
  switch (fieldType) {
    case "text":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    default:
      return "Ingresa un valor";
  }
};

// Función helper para mapear tipos de campo a tipos de variable
const mapFieldTypeToVariableType = (fieldType: string): string => {
  switch (fieldType) {
    case "text":
      return "string";
    case "number":
      return "number";
    case "date":
      return "date";
    case "boolean":
      return "boolean";
    default:
      return "string";
  }
};

export const DocumentFieldValueInput = ({
  field,
  value,
  onValueChange,
  inputVariables,
  group,
  placeholder,
  showInputIcon = true,
}: DocumentFieldValueInputProps) => {
  // Para campos de fecha cuando no es una variable
  if (field.inputType !== "variable" && field.type === "date") {
    // Adjust date to compensate for timezone offset
    const adjustedDate = (() => {
      if (!value || typeof value !== "string") return value as string;

      try {
        // If date is in ISO format, add local time to avoid UTC conversion
        const dateStr = value as string;
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // YYYY-MM-DD format - add local time to avoid UTC conversion
          return `${dateStr}T12:00:00`;
        }
        return dateStr;
      } catch {
        return value as string;
      }
    })();

    return (
      <ComparativeValueInputCalendar
        group={group}
        defaultDate={adjustedDate}
        onChangeDate={onValueChange}
      />
    );
  }

  // Para campos boolean cuando no es una variable
  if (field.inputType !== "variable" && field.type === "boolean") {
    return (
      <div
        className={cn(
          "px-3 py-2 flex items-center w-full border-neutral leading-3xl text-body-loud bg-neutral-object-accent-silent",
          group === "right"
            ? "border-r border-y rounded-r-lg"
            : "border-r border-b rounded-ee-lg",
        )}
      >
        <Switch
          id={`switch-${field.name}`}
          checked={value === "true"}
          onCheckedChange={(checked) => {
            onValueChange(checked ? "true" : "false");
          }}
        />
        <span className="text-body font-normal ml-2">
          {value === "true" ? "Verdadero" : "Falso"}
        </span>
      </div>
    );
  }

  // Para campos de texto/número cuando no es una variable
  if (field.inputType !== "variable") {
    return (
      <div className="relative w-full">
        <Input
          group={group}
          className={cn("w-full", showInputIcon && "pl-9")}
          placeholder={placeholder ?? getPlaceholder(field.type)}
          value={value as string}
          maxLength={getMaxLength(field.type)}
          type={field.type === "number" ? "number" : "text"}
          onChange={(e) => {
            let newValue = e.target.value;

            // Aplicar restricciones según el tipo
            if (field.type === "number") {
              // Solo permitir números
              newValue = newValue.replace(/[^0-9.-]/g, "");
              // Limitar a máximo 15 caracteres
              if (newValue.length > 15) {
                newValue = newValue.substring(0, 15);
              }
            } else if (field.type === "text") {
              // Limitar a máximo 255 caracteres
              if (newValue.length > 255) {
                newValue = newValue.substring(0, 255);
              }
            }

            onValueChange(newValue);
          }}
        />
        {showInputIcon && (
          <Icon
            type="far"
            name={getIconName(field.type)}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-body"
          />
        )}
      </div>
    );
  }

  // Para campos cuando es una variable
  if (field.inputType === "variable") {
    return (
      <VariableSelector
        placeholder="Elegir variable"
        variables={inputVariables}
        selectedVariable={value as Variable}
        onSelectVariable={(variable) => {
          onValueChange(variable || "");
        }}
        triggerGroup={group}
        filterByType={mapFieldTypeToVariableType(field.type)}
      />
    );
  }

  // Fallback
  return null;
};
